import moment from 'moment';

import {
  BATCH_ACTIVATE_DISABLED_REASON,
  BATCH_EDIT_STATUS_DISABLED_REASON,
  BATCH_LOCK_REASON,
  BATCH_OPT_OUT_DISABLED_REASON,
  COVERAGE_TIME_ZONE,
  DISABLED_REASONS,
  GROUP_FAILED_LABEL,
  GROUP_OUTCOME,
  GROUP_SKIPPED_LABEL,
  ONBOARDING_REASON,
  ONBOARDING_REASON_LABELS,
  OPERATOR_ACTOR_PREFIX,
  QUEUE_ACTION,
  QUEUE_DATE_FORMAT,
  QUEUE_ERROR_FALLBACK,
  QUEUE_ROW_STATUS_MIXED,
  QUEUE_STATUS,
  ROW_ACTIONS,
  STATUS_LABELS,
  SYSTEM_ACTOR,
  VALUE_PLACEHOLDER,
  WALL_CLOCK_DISPLAY_FORMAT,
  WALL_CLOCK_PARSE_FORMAT,
} from '../constants/onboardingQueue';

const wallClockMoment = value => {
  if (!value) return null;
  const parsed = moment(value, [WALL_CLOCK_PARSE_FORMAT, QUEUE_DATE_FORMAT], true);
  return parsed.isValid() ? parsed : null;
};

export const wallClockDate = value => {
  const parsed = wallClockMoment(value);
  return parsed ? parsed.format(QUEUE_DATE_FORMAT) : VALUE_PLACEHOLDER;
};

export const wallClockDateTime = value => {
  const parsed = wallClockMoment(value);
  return parsed ? parsed.format(WALL_CLOCK_DISPLAY_FORMAT) : VALUE_PLACEHOLDER;
};

export const daysAgoLabel = (createdAt, now = moment()) => {
  const parsed = wallClockMoment(createdAt);
  if (!parsed) return VALUE_PLACEHOLDER;

  const days = moment(now).startOf('day').diff(parsed.startOf('day'), 'days');
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
};

export const operatorLabel = updatedBy => {
  if (!updatedBy) return VALUE_PLACEHOLDER;

  const actor = updatedBy.startsWith(OPERATOR_ACTOR_PREFIX)
    ? updatedBy.slice(OPERATOR_ACTOR_PREFIX.length)
    : updatedBy;
  return actor.trim() || VALUE_PLACEHOLDER;
};

export const statusLabel = status => {
  if (!status) return VALUE_PLACEHOLDER;
  return STATUS_LABELS[status] || status;
};

export const contactInfo = (email, phone) => ({
  email: email || VALUE_PLACEHOLDER,
  phone: phone || VALUE_PLACEHOLDER,
});

export const toDateBound = bound => {
  if (!bound || !moment.isMoment(bound) || !bound.isValid()) return null;
  return bound.format(QUEUE_DATE_FORMAT);
};

export const queueErrorMessage = error => {
  const message = error && error.message ? String(error.message).trim() : '';
  return message || QUEUE_ERROR_FALLBACK;
};

export const reasonLabel = reason => {
  if (!reason) return VALUE_PLACEHOLDER;
  return ONBOARDING_REASON_LABELS[reason] || reason;
};

export const toReasonSelection = reason => {
  const stored = reason == null ? '' : String(reason);
  if (!stored) return { reason: null, otherText: '' };
  if (ONBOARDING_REASON_LABELS[stored]) return { reason: stored, otherText: '' };
  return { reason: ONBOARDING_REASON.OTHER, otherText: stored };
};

export const attemptCount = record => {
  const count = record ? record.noteCount : null;
  return typeof count === 'number' && Number.isFinite(count) ? count : VALUE_PLACEHOLDER;
};

const coverageDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: COVERAGE_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export const coverageStartDate = (now = Date.now()) => {
  const parts = coverageDateFormatter.formatToParts(now);
  const part = type => parts.find(p => p.type === type).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};

export const mutationErrorMessage = (payload, error) => {
  const messages = payload && Array.isArray(payload.errors) ? payload.errors : [];
  const joined = messages.map(message => String(message).trim()).filter(Boolean).join(' ');
  return joined || queueErrorMessage(error);
};

export const hasAgreedPrice = price => typeof price === 'number' && Number.isFinite(price);

export const priceProps = price => ({
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  value: price,
});

export const isBatchRow = row => row.dealers.length > 1;

const latestBy = (dealers, field) => dealers.reduce((latest, dealer) => {
  const stamp = wallClockMoment(dealer[field]);
  if (!stamp) return latest;
  if (!latest || stamp.isAfter(latest.stamp)) return { dealer, stamp };
  return latest;
}, null);

export const rowRecord = row => {
  const [first] = row.dealers;
  if (!isBatchRow(row)) return first;

  const statuses = new Set(row.dealers.map(dealer => dealer.status));
  const completed = latestBy(row.dealers, 'completedAt');
  const changed = latestBy(row.dealers, 'updatedAt');

  return {
    ...first,
    isBatch: true,
    status: statuses.size === 1 ? first.status : QUEUE_ROW_STATUS_MIXED,
    completedAt: completed ? completed.dealer.completedAt : null,
    updatedBy: (changed ? changed.dealer : first).updatedBy,
  };
};

export const dealerLockReason = dealer => {
  if (dealer.status === QUEUE_STATUS.ACCEPTED) return BATCH_LOCK_REASON.ACCEPTED;
  if (
    dealer.status === QUEUE_STATUS.DECLINED
    && dealer.reason === ONBOARDING_REASON.OFFER_CANCELLED
    && dealer.updatedBy === SYSTEM_ACTOR
  ) {
    return BATCH_LOCK_REASON.REPLACED;
  }
  if (!hasAgreedPrice(dealer.subscriptionPrice)) return BATCH_LOCK_REASON.NO_PRICE;
  return null;
};

export const isActivatable = dealer => !dealerLockReason(dealer);

export const pendingDealers = row => row.dealers.filter(dealer => dealer.status === QUEUE_STATUS.PENDING);

const isAccepted = dealer => dealer.status === QUEUE_STATUS.ACCEPTED;

const OPEN_ACTION = { disabled: false, reason: null };

const lockedWhen = (locked, reason) => (locked ? { disabled: true, reason } : OPEN_ACTION);

const singleActionState = (action, dealer) => lockedWhen(
  isAccepted(dealer) && (action === QUEUE_ACTION.ACTIVATE || action === QUEUE_ACTION.EDIT_STATUS),
  DISABLED_REASONS[action]
);

const BATCH_ACTION_STATE = {
  [QUEUE_ACTION.ACTIVATE]: row => lockedWhen(
    !row.dealers.some(isActivatable),
    BATCH_ACTIVATE_DISABLED_REASON
  ),
  [QUEUE_ACTION.EDIT_STATUS]: () => lockedWhen(true, BATCH_EDIT_STATUS_DISABLED_REASON),
  [QUEUE_ACTION.OPT_OUT]: row => lockedWhen(
    pendingDealers(row).length === 0,
    BATCH_OPT_OUT_DISABLED_REASON
  ),
};

export const rowActions = (view, row) => ROW_ACTIONS[view].filter(action => (
  action !== QUEUE_ACTION.SEE_DETAILS || isBatchRow(row)
));

export const rowActionState = (action, row) => {
  if (!isBatchRow(row)) return singleActionState(action, row.dealers[0]);
  const state = BATCH_ACTION_STATE[action];
  return state ? state(row) : OPEN_ACTION;
};

const countOf = value => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const FAILED_OUTCOMES = [GROUP_OUTCOME.CONFLICT, GROUP_OUTCOME.FAILED];

const failureLine = (result, dealers) => {
  const dealer = dealers.find(item => item.flatRateOfferId === result.flatRateOfferId);
  const who = (dealer && dealer.customerId) || result.dealer5Mil || VALUE_PLACEHOLDER;
  return `${who}: ${result.message || VALUE_PLACEHOLDER}`;
};

export const groupResultNotice = (action, { verb, dealers }) => {
  const group = action || {};
  const success = countOf(group.successCount);
  const skipped = countOf(group.skippedCount);
  const failure = countOf(group.failureCount);
  const parts = [
    [success, verb],
    [skipped, GROUP_SKIPPED_LABEL],
    [failure, GROUP_FAILED_LABEL],
  ].filter(([count]) => count > 0).map(([count, label]) => `${count} ${label}`);
  const failures = (group.results || [])
    .filter(result => FAILED_OUTCOMES.includes(result.outcome))
    .map(result => failureLine(result, dealers));

  return {
    content: parts.length > 0 ? parts.join(', ') : `0 ${verb}`,
    list: failures.length > 0 ? failures : null,
    warning: failure > 0,
  };
};

export const dealerLabel = dealer => (
  `${dealer.dealerName || VALUE_PLACEHOLDER} (${dealer.customerId || VALUE_PLACEHOLDER})`
);

export const productLabel = record => record.productName || record.productType || VALUE_PLACEHOLDER;
